import { Locale, RedisTtlConfig } from '@headless-workspace/config';
import {
    CachedRepository,
    PreviewOptions,
    Result,
    ResultEmpty,
    ResultFailureFromApiError,
    ResultSuccess,
} from '@headless-workspace/core-domain';
import {
    memoryCacheAuthStrategy,
    memoryCacheQueryParamStrategy,
    memoryCacheRefreshTokenStrategy,
} from '@headless-workspace/data';
import { CachingHelpers, DecoderHelpers, PredicateHelpers } from '@headless-workspace/utils';
import { CategoriesContainerDTO, CategoryDTO } from './CategoriesDTO';
import { CategoryDeepLevel } from './CategoryDeepLevel';
import { CategoryValue, mapCategory } from './CategoryValue';

type FetchAllCategoriesParams = {
    locale: Locale;
};
const CACHE_SERVICE_CATEGORIES_PREFIX = 'catalog';
const CACHE_SERVICE_CATEGORIES_KEY = 'categories';
const CACHE_SERVICE_CATEGORIES_LEVEL_KEY = 'categories-level';

export interface CatalogCategoriesRepositorySpec {
    fetchAllCategories(params: FetchAllCategoriesParams): Promise<Result<CategoryValue[]>>;

    fetchCategoriesByLevel(locale: Locale, level: CategoryDeepLevel): Promise<Result<CategoryValue[]>>;
}

export class CatalogCategoriesRepository extends CachedRepository implements CatalogCategoriesRepositorySpec {
    path = '/catalog/v1';

    constructor() {
        super(memoryCacheAuthStrategy, memoryCacheRefreshTokenStrategy, memoryCacheQueryParamStrategy);
    }

    private async fetchRemoteAllCategories({
        locale,
        isPreviewable = false,
    }: FetchAllCategoriesParams & PreviewOptions): Promise<Result<CategoryValue[]>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/categories`, {
                locale,
                level: CategoryDeepLevel.Ternary,
                isPreviewable,
            });
            const categoriesContainer = CategoriesContainerDTO.parse(response);
            const categories = DecoderHelpers.safeParseArray(categoriesContainer.categories, CategoryDTO);

            if (categories.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(categories.map(mapCategory).filter(PredicateHelpers.notNullOrUndefined));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async fetchAllCategories({ locale }: { locale: Locale }): Promise<Result<CategoryValue[]>> {
        if (this.isPreviewModeEnabled()) {
            return this.fetchRemoteAllCategories({ isPreviewable: true, locale });
        }

        const params = { locale };

        return this.withCache(
            locale,
            CACHE_SERVICE_CATEGORIES_PREFIX,
            CachingHelpers.createCacheKey(CACHE_SERVICE_CATEGORIES_KEY, params),
            {
                ...RedisTtlConfig,
                tags: CachingHelpers.createTags(CACHE_SERVICE_CATEGORIES_PREFIX, CACHE_SERVICE_CATEGORIES_KEY, params),
            },
            () => this.fetchRemoteAllCategories(params),
        );
    }

    async fetchRemoteCategoriesByLevel(locale: Locale, level: CategoryDeepLevel): Promise<Result<CategoryValue[]>> {
        try {
            const response = await this.datasource.getResource(`${this.path}/categories`, {
                locale,
                level,
                isPreviewable: false,
            });
            const categoriesContainer = CategoriesContainerDTO.parse(response);
            const categories = DecoderHelpers.safeParseArray(categoriesContainer.categories, CategoryDTO);

            if (categories.length === 0) {
                return ResultEmpty();
            }

            return ResultSuccess(categories.map(mapCategory).filter(PredicateHelpers.notNullOrUndefined));
        } catch (error) {
            const apiError = this.handleApiError(error);
            return ResultFailureFromApiError(apiError, { locale });
        }
    }

    async fetchCategoriesByLevel(locale: Locale, level: CategoryDeepLevel): Promise<Result<CategoryValue[]>> {
        const params = { locale, level };

        return this.withCache(
            locale,
            CACHE_SERVICE_CATEGORIES_PREFIX,
            CachingHelpers.createCacheKey(CACHE_SERVICE_CATEGORIES_LEVEL_KEY, params),
            {
                ...RedisTtlConfig,
                tags: CachingHelpers.createTags(
                    CACHE_SERVICE_CATEGORIES_PREFIX,
                    CACHE_SERVICE_CATEGORIES_LEVEL_KEY,
                    params,
                ),
            },
            () => this.fetchRemoteCategoriesByLevel(locale, level),
        );
    }
}
